import React, { useEffect } from 'react'
import { View } from 'react-native'

// services
import Analytics from 'APP/Services/Analytics'

// Components
import Button from 'APP/Converse/Button'

import styled from 'styled-components/native'
import Typography from 'APP/Converse/Typography'

const StyledBodyContainer = styled(View)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`

const StyledBodyAction = styled(Typography).attrs(() => ({
  variant: 'bodyNormal',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
`

const StyledLink = styled(Typography).attrs(() => ({
  variant: 'link',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-decoration-line: underline;
  text-transform: capitalize;
`

const StyledContainer = styled(View)`
  display: flex;
  border-radius: ${({ theme }) => theme.metrics.borderRadius.medium}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  flex-direction: column;
  align-items: center;
  ${({ theme }) => theme.shadows.soft};
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-left: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  padding-right: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-left: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  margin-right: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
`

const StyledContent = styled(View)`
  display: flex;
  align-items: center;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const StyledTitle = styled(Typography).attrs(() => ({
  variant: 'subheaderBold',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`

const StyledSubtitle = styled(Typography).attrs(() => ({
  variant: 'bodyNormal',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
`

export type InfoCardProps = {
  analyticsName?: string
  buttonText: string
  onButtonPressed?: () => void
  buttonAnalyticsName?: string
  busy: boolean
  icon: React.ReactNode
  title: string
  subtitle?: string
  customSubtitle?: string
  linkText?: string
  linkCta?: string
  onLinkPressed?: () => void
  testId?: string
}

const InfoCard = ({
  analyticsName,
  buttonText,
  onButtonPressed,
  buttonAnalyticsName,
  busy,
  icon,
  title,
  subtitle,
  customSubtitle,
  linkText,
  linkCta,
  onLinkPressed,
  testId,
}: InfoCardProps) => {
  const hasLinkCta = linkText && linkCta && onLinkPressed
  const updateBannerContext = () => {
    if (analyticsName) {
      const bannerPayload = { banner_name: analyticsName }
      Analytics.attachEntity('banner_context', bannerPayload)
    } else {
      Analytics.detachEntity('banner_context')
    }
  }

  useEffect(() => {
    updateBannerContext()
  }, [])

  const renderLinkCta = () => {
    return (
      <StyledBodyContainer>
        <StyledBodyAction>
          {linkText}
          <StyledLink onPress={() => onLinkPressed?.()}>{linkCta}</StyledLink>
        </StyledBodyAction>
      </StyledBodyContainer>
    )
  }

  return (
    <StyledContainer testID={testId}>
      {icon}
      <StyledContent testID="content">
        <StyledTitle>{title}</StyledTitle>
        {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
        {customSubtitle}
        {!!buttonText && (
          <Button
            variant="oomph"
            title={buttonText}
            onPress={onButtonPressed}
            analyticsName={buttonAnalyticsName}
            isLoading={busy}
          />
        )}
      </StyledContent>
      {hasLinkCta && renderLinkCta()}
    </StyledContainer>
  )
}

export default InfoCard
